/**
 * EPL Custom Hooks - Estado y lógica reutilizable
 * 
 * Hooks para:
 * - Gestión de laboratorios
 * - Estados de sesiones 
 * - Validación en tiempo real
 * - Cache y optimización
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { eplApi } from '../services/epl_api';

// =================== useEplLabs ===================

/**
 * Hook para gestionar lista de laboratorios
 */
export const useEplLabs = (initialParams = {}) => {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchLabs = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await eplApi.listLabs({ ...initialParams, ...params });
      
      setLabs(response.data || []);
      setPagination(prev => ({ ...prev, ...(response.pagination || {}) }));
    } catch (err) {
      setError(err.message);
      console.error('❌ Error fetching labs:', err);
    } finally {
      setLoading(false);
    }
  }, []); // ← FIX: Eliminar initialParams de las dependencias

  const createLab = useCallback(async (labData) => {
    try {
      setLoading(true);
      const response = await eplApi.createLab(labData);
      
      // Refrescar lista después de crear
      await fetchLabs();
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchLabs]);

  const deleteLab = useCallback(async (labId) => {
    try {
      setLoading(true);
      await eplApi.deleteLab(labId);
      
      // Refrescar lista después de eliminar
      await fetchLabs();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchLabs]);

  // Eliminado useEffect para evitar fetch duplicado
  // El componente que use este hook debe llamar a fetchLabs() manualmente

  return {
    labs,
    loading,
    error,
    pagination,
    actions: {
      fetchLabs,
      createLab,
      deleteLab,
      refresh: () => fetchLabs()
    }
  };
};

// =================== useEplLab ===================

/**
 * Hook para gestionar un laboratorio específico
 */
export const useEplLab = (labId) => {
  const [lab, setLab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchLab = useCallback(async () => {
    if (!labId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await eplApi.getLab(labId);
      setLab(response.data);
      setHasChanges(false);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching lab:', err);
    } finally {
      setLoading(false);
    }
  }, [labId]);

  const updateLab = useCallback(async (updateData) => {
    try {
      setLoading(true);
      const response = await eplApi.updateLab(labId, updateData);
      
      setLab(response.data);
      setHasChanges(false);
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [labId]);

  const updateLabLocal = useCallback((updates) => {
    setLab(prev => prev ? { ...prev, ...updates } : null);
    setHasChanges(true);
  }, []);

  useEffect(() => {
    fetchLab();
  }, [fetchLab]);

  return {
    lab,
    loading,
    error,
    hasChanges,
    actions: {
      fetchLab,
      updateLab,
      updateLabLocal,
      refresh: () => fetchLab()
    }
  };
};

// =================== useEplSession ===================

/**
 * Hook para gestionar sesiones de laboratorio
 */
export const useEplSession = () => {
  const [session, setSession] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const startSession = useCallback(async (labId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await eplApi.startLabSession(labId);
      
      setSession(response.data);
      setResponses(response.data.responses || {});
      
      // ✅ Guardar stateId en localStorage para recuperar sesión después
      const stateId = response.data.id || response.data._id;
      if (stateId) {
        localStorage.setItem(`epl_session_${labId}`, stateId);
        console.log(`✅ Sesión guardada en localStorage: epl_session_${labId} = ${stateId}`);
      }
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateResponses = useCallback(async (newResponses, metadata = {}) => {
    if (!session) return;
    
    try {
      setLoading(true);
      
      const response = await eplApi.updateSessionResponses(
        session.id || session._id?.toString(), 
        newResponses,
        metadata // Pasar metadata adicional (como aiAttempts)
      );
      
      setSession(response.data);
      setResponses(response.data.responses || {});
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [session]);

  const updateResponseLocal = useCallback((fieldId, value) => {
    setResponses(prev => ({
      ...prev,
      [fieldId]: value
    }));
  }, []);

  const loadSession = useCallback(async (stateId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await eplApi.getSessionState(stateId);
      
      setSession(response.data);
      setResponses(response.data.responses || {});
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    session,
    responses,
    loading,
    error,
    actions: {
      startSession,
      updateResponses,
      updateResponseLocal,
      loadSession,
      clearSession: () => {
        setSession(null);
        setResponses({});
      }
    }
  };
};

// =================== useEplValidation ===================

/**
 * Hook para validación en tiempo real
 */
export const useEplValidation = () => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const debounceTimer = useRef(null);

  const validateLabConfig = useCallback(async (labConfig, debounceMs = 500) => {
    // Limpiar timer anterior
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    return new Promise((resolve) => {
      debounceTimer.current = setTimeout(async () => {
        try {
          setIsValidating(true);
          setValidationErrors({});
          
          const response = await eplApi.validateLabConfig(labConfig);
          
          if (!response.valid) {
            setValidationErrors(response.errors || {});
          }
          
          resolve(response);
        } catch (err) {
          setValidationErrors({ general: err.message });
          resolve({ valid: false, errors: { general: err.message } });
        } finally {
          setIsValidating(false);
        }
      }, debounceMs);
    });
  }, []);

  const clearValidation = useCallback(() => {
    setValidationErrors({});
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
  }, []);

  return {
    isValidating,
    validationErrors,
    validateLabConfig,
    clearValidation
  };
};

// =================== useEplHealth ===================

/**
 * Hook para monitorear salud del sistema EPL
 */
export const useEplHealth = (intervalMs = 30000) => {
  const [health, setHealth] = useState({
    server: 'unknown',
    database: 'unknown',
    lastCheck: null
  });
  const intervalRef = useRef(null);

  const checkHealth = useCallback(async () => {
    try {
      const [serverHealth, dbHealth] = await Promise.all([
        eplApi.checkHealth().catch(() => ({ status: 'error' })),
        eplApi.checkDatabase().catch(() => ({ database: { status: 'error' } }))
      ]);

      setHealth({
        server: serverHealth.status === 'OK' ? 'ok' : 'error',
        database: dbHealth.database?.status === 'healthy' ? 'connected' : 'error',
        lastCheck: new Date()
      });
    } catch (err) {
      setHealth({
        server: 'error',
        database: 'error',
        lastCheck: new Date()
      });
    }
  }, []);

  useEffect(() => {
    checkHealth();
    
    if (intervalMs > 0) {
      intervalRef.current = setInterval(checkHealth, intervalMs);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkHealth, intervalMs]);

  return {
    health,
    checkHealth
  };
};